import { LANGUAGE_KEYS } from "./constants";
import { createEmptyRichText, parseRichText } from "./formatUtils";

const RICH_TEXT_FIELDS = [
  "jobDescription",
  "subDescription",
  "reportsTo",
  "term",
  "deadline",
];

const SECTION_FIELDS = {
  aboutUniversity: "description",
  disciplines: "arguments",
  applicationProcess: "arguments",
};

const RICH_TEXT_LIST_FIELDS = [
  "arguments",
  "qualification",
  "experience",
  "skills",
];

export const createEmptyMultiLanguage = () => ({
  [LANGUAGE_KEYS.UZ]: "",
  [LANGUAGE_KEYS.RU]: "",
  [LANGUAGE_KEYS.EN]: "",
});

export const createEmptyRichMultiLanguage = () => ({
  [LANGUAGE_KEYS.UZ]: createEmptyRichText(),
  [LANGUAGE_KEYS.RU]: createEmptyRichText(),
  [LANGUAGE_KEYS.EN]: createEmptyRichText(),
});

const withAllLanguages = (value) => ({
  ...createEmptyMultiLanguage(),
  ...(value ?? {}),
});

const toRichMultiLanguage = (value) => ({
  [LANGUAGE_KEYS.UZ]: parseRichText(value?.[LANGUAGE_KEYS.UZ]),
  [LANGUAGE_KEYS.RU]: parseRichText(value?.[LANGUAGE_KEYS.RU]),
  [LANGUAGE_KEYS.EN]: parseRichText(value?.[LANGUAGE_KEYS.EN]),
});

const fromRichMultiLanguage = (value) => ({
  [LANGUAGE_KEYS.UZ]: JSON.stringify(value?.[LANGUAGE_KEYS.UZ] ?? []),
  [LANGUAGE_KEYS.RU]: JSON.stringify(value?.[LANGUAGE_KEYS.RU] ?? []),
  [LANGUAGE_KEYS.EN]: JSON.stringify(value?.[LANGUAGE_KEYS.EN] ?? []),
});

const toRichList = (list) =>
  (Array.isArray(list) ? list : []).map(toRichMultiLanguage);

const fromRichList = (list) =>
  (Array.isArray(list) ? list : []).map(fromRichMultiLanguage);

// Slate tugunlaridan matnni yig'ib oladi.
const richTextToPlain = (nodes) =>
  (Array.isArray(nodes) ? nodes : [])
    .map((node) =>
      typeof node?.text === "string"
        ? node.text
        : richTextToPlain(node?.children),
    )
    .join("");

// Jadval kabi formatlashsiz joylar uchun: saqlangan qiymatdan oddiy matn.
export const richTextToPlainText = (value) =>
  richTextToPlain(parseRichText(value)).trim();

const isRichRowFilled = (row) =>
  Object.values(row ?? {}).some(
    (value) => richTextToPlain(value).trim().length > 0,
  );

const createEmptySection = (listKey) => ({
  title: createEmptyRichMultiLanguage(),
  [listKey]: [],
});

const toRichSection = (value, listKey) => ({
  title: toRichMultiLanguage(value?.title),
  [listKey]: toRichList(value?.[listKey]),
});

const fromRichSection = (value, listKey) => ({
  title: fromRichMultiLanguage(value?.title),
  [listKey]: fromRichList((value?.[listKey] ?? []).filter(isRichRowFilled)),
});

// Optional bo'limlar: hech narsa yozilmagan bo'lsa umuman yuborilmaydi.
const isSectionFilled = (value, listKey) =>
  isRichRowFilled(value?.title) ||
  (value?.[listKey] ?? []).some(isRichRowFilled);

export const createEmptyVacancy = () => ({
  jobTitle: createEmptyMultiLanguage(),
  jobDescription: createEmptyRichMultiLanguage(),
  subDescription: createEmptyRichMultiLanguage(),
  reportsTo: createEmptyRichMultiLanguage(),
  term: createEmptyRichMultiLanguage(),
  deadline: createEmptyRichMultiLanguage(),
  aboutUniversity: createEmptySection("description"),
  disciplines: createEmptySection("arguments"),
  applicationProcess: createEmptySection("arguments"),
  arguments: [],
  qualification: [],
  experience: [],
  skills: [],
  vacancyType: "",
  orderIndex: "",
  isActive: true,
});

// API javobida maydonlar null yoki yetishmasligi mumkin — forma ularning
// hammasi mavjud bo'lishiga tayanadi.
export const normalizeVacancy = (vacancy) => {
  const richFields = Object.fromEntries(
    RICH_TEXT_FIELDS.map((field) => [
      field,
      toRichMultiLanguage(vacancy?.[field]),
    ]),
  );
  const richLists = Object.fromEntries(
    RICH_TEXT_LIST_FIELDS.map((field) => [field, toRichList(vacancy?.[field])]),
  );

  return {
    jobTitle: withAllLanguages(vacancy?.jobTitle),
    ...richFields,
    ...Object.fromEntries(
      Object.entries(SECTION_FIELDS).map(([field, listKey]) => [
        field,
        toRichSection(vacancy?.[field], listKey),
      ]),
    ),
    ...richLists,
    vacancyType: vacancy?.vacancyType ?? "",
    orderIndex: vacancy?.orderIndex ?? "",
    isActive: vacancy?.isActive ?? null,
  };
};

export const buildVacancyRequestBody = (form) => {
  const richFields = Object.fromEntries(
    RICH_TEXT_FIELDS.map((field) => [
      field,
      fromRichMultiLanguage(form[field]),
    ]),
  );
  const richLists = Object.fromEntries(
    RICH_TEXT_LIST_FIELDS.map((field) => [
      field,
      fromRichList((form[field] ?? []).filter(isRichRowFilled)),
    ]),
  );

  return {
    jobTitle: form.jobTitle,
    ...richFields,
    ...Object.fromEntries(
      Object.entries(SECTION_FIELDS)
        .filter(([field, listKey]) => isSectionFilled(form[field], listKey))
        .map(([field, listKey]) => [
          field,
          fromRichSection(form[field], listKey),
        ]),
    ),
    ...richLists,
    vacancyType: form.vacancyType,
    orderIndex: form.orderIndex === "" ? null : Number(form.orderIndex),
    isActive: form.isActive,
  };
};

// Server o'zi boshqaradigan maydonlar qayta yuborilmaydi.
const SERVER_FIELDS = ["id", "createdAt", "modifiedAt"];

const stripServerFields = (vacancy) =>
  Object.fromEntries(
    Object.entries(vacancy ?? {}).filter(
      ([key]) => !SERVER_FIELDS.includes(key),
    ),
  );

// Faqat tahrirlangan maydonlarni serializatsiya qiladi.
const serializePatchField = (field, value) => {
  if (SECTION_FIELDS[field]) {
    return fromRichSection(value, SECTION_FIELDS[field]);
  }

  if (RICH_TEXT_LIST_FIELDS.includes(field)) {
    return fromRichList((value ?? []).filter(isRichRowFilled));
  }

  if (RICH_TEXT_FIELDS.includes(field)) return fromRichMultiLanguage(value);

  if (field === "orderIndex") return value === "" ? null : Number(value);

  return value;
};

// Detal sahifada bloklar alohida saqlanadi. PUT qisman qabul qiladimi noma'lum,
// shuning uchun to'liq obyekt yuboriladi — lekin tegilmagan maydonlar serverdan
// kelgan ko'rinishida qoladi, aks holda bitta blokni saqlash butun yozuvni
// yangi formatga o'tkazib yuboradi.
export const buildVacancyBlockRequestBody = (vacancy, patch) => ({
  ...stripServerFields(vacancy),
  ...Object.fromEntries(
    Object.entries(patch).map(([field, value]) => [
      field,
      serializePatchField(field, value),
    ]),
  ),
});
